import React from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import { TableCell, Checkbox, Icon, Tooltip, Link, Box, Chip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import QrCodeIcon from '@mui/icons-material/QrCode';
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';
import ReplyIcon from '@mui/icons-material/Reply';
import M from 'messages';
import TableConfigs from 'constants/table-configs';
import { getCellPadding, getWidth } from 'helpers/tableStyleHelper';

import CloseIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';

import classes from './styles';

const actionsStyles = {
  paddingRight: '2px',
  paddingLeft: '2px',
};

const STATUS_CHIP_TOOLTIP_PATH = {
  Live: 'wines.statusChipTooltip.live',
  Pending: 'wines.statusChipTooltip.pending',
  Locked: 'wines.statusChipTooltip.locked',
  Unlisted: 'wines.statusChipTooltip.unlisted',
};

function CustomTableCell({
  isCheckbox = false,
  isItemSelected = false,
  isEditAction = false,
  isQRAction = false,
  isInMarketAction = false,
  isRemoveAction = false,
  isResponseAction = false,
  isEmptyCell = false,
  cellItem = {},
  data = {},
  loading = false,
  handleClickIcon = null,
  isOpenAction = false,
  isCloneAction = false,
  openPath = '',
  filteredParams = {},
  isShow = null,
  stickyHeader = false,
  emptyRowSlot = null,
}) {
  const onIconClick = (e, values) => {
    e.stopPropagation();
    handleClickIcon(values);
  };

  const onEditIconClick = (e, values) => {
    e.stopPropagation();
    handleClickIcon(values);
  };

  const isSortedField = () =>
    filteredParams.params?.sort?.field === cellItem.id ||
    (cellItem.orderField && filteredParams.params?.sort?.field === cellItem.orderField) ||
    cellItem.isActiveStyle;

  const defaultCell = () => (
    <TableCell
      sx={{
        ...classes.tableCellItem,
        ...(isSortedField() && classes.sortedCeil),
        ...(cellItem.withEllipsis && classes.ellipsis),
      }}
      style={{
        ...getCellPadding(cellItem.cellPaddingRight, cellItem.cellPaddingLeft),
        ...(stickyHeader ? { minWidth: cellItem.minWidth } : getWidth(cellItem.width)),
      }}
      align={cellItem.textAlign || 'left'}>
      {data[cellItem.id]}
    </TableCell>
  );

  const cellView = () => {
    if (isEmptyCell) {
      return (
        <TableCell
          style={getCellPadding(TableConfigs.defaultCellPadding, TableConfigs.defaultCellPadding)}
          colSpan={20}
          sx={isSortedField() ? classes.sortedCeil : {}}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              columnGap: 2,
              rowGap: 1,
            }}>
            {!loading && <Box component="span">{M.get('table.noResult')}</Box>}
            {!loading && emptyRowSlot}
          </Box>
        </TableCell>
      );
    }
    if (isCheckbox) {
      return (
        <TableCell
          component="th"
          scope="row"
          style={{ width: '24px', paddingRight: '16px', paddingLeft: '24px' }}
          sx={isSortedField() ? classes.sortedCeil : {}}>
          {' '}
          <Checkbox onClick={e => onIconClick(e, data)} checked={isItemSelected} color="primary" />{' '}
        </TableCell>
      );
    }
    if (isInMarketAction) {
      return (
        <TableCell
          align="left"
          sx={{
            ...classes.tableCellAction,
            ...(isSortedField() && classes.sortedCeil),
          }}
          style={{
            ...actionsStyles,
          }}>
          {(!isShow || isShow(data)) && (
            <Tooltip title="Activate for Market">
              <PublishedWithChangesIcon onClick={e => onEditIconClick(e, data)} />
            </Tooltip>
          )}
        </TableCell>
      );
    }
    if (isEditAction) {
      return (
        <TableCell
          align="left"
          sx={{
            ...classes.tableCellAction,
            ...(isSortedField() && classes.sortedCeil),
          }}
          style={{
            ...actionsStyles,
          }}>
          {' '}
          <Tooltip title="Edit">
            <EditIcon onClick={e => onEditIconClick(e, data)} />
          </Tooltip>
        </TableCell>
      );
    }
    if (isQRAction) {
      return (
        <TableCell
          align="left"
          sx={{
            ...classes.tableCellAction,
            ...(isSortedField() && classes.sortedCeil),
          }}
          style={{
            ...actionsStyles,
          }}>
          {(!isShow || isShow(data)) && (
            <Tooltip title="Open QR Code">
              <QrCodeIcon onClick={e => onEditIconClick(e, data)} />
            </Tooltip>
          )}
        </TableCell>
      );
    }
    if (isRemoveAction) {
      return (
        <TableCell
          align="left"
          sx={{
            ...classes.tableCellAction,
            ...(isSortedField() && classes.sortedCeil),
            ...classes.removeCell,
          }}
          style={{
            ...actionsStyles,
          }}>
          {(!isShow || isShow(data)) && (
            <Tooltip title="Remove">
              <RemoveCircleIcon onClick={e => onEditIconClick(e, data)} />
            </Tooltip>
          )}
        </TableCell>
      );
    }
    if (isResponseAction) {
      return (
        <TableCell
          align="left"
          sx={{
            ...classes.tableCellAction,
            ...(isSortedField() && classes.sortedCeil),
          }}
          style={{
            ...actionsStyles,
          }}>
          <Tooltip title="Reply">
            <ReplyIcon onClick={e => onEditIconClick(e, data)} />
          </Tooltip>
        </TableCell>
      );
    }
    if (isOpenAction) {
      return (
        <TableCell
          sx={classes.tableCellAction}
          align="left"
          style={{
            ...actionsStyles,
          }}>
          <RouterLink to={openPath}>
            <ArrowForwardIosIcon />
          </RouterLink>
        </TableCell>
      );
    }
    if (isCloneAction) {
      return (
        <TableCell
          align="left"
          sx={{
            ...classes.tableCellAction,
            ...(isSortedField() && classes.sortedCeil),
          }}
          style={{
            ...actionsStyles,
          }}>
          {(!isShow || isShow(data)) && (
            <Tooltip title="Copy">
              <Icon sx={classes.copyIcon} onClick={e => onEditIconClick(e, data)}>
                content_copy
              </Icon>
            </Tooltip>
          )}
        </TableCell>
      );
    }
    switch (cellItem.type) {
      case 'statusChip': {
        const statusLabel = data[cellItem.id];
        if (!statusLabel) {
          return defaultCell();
        }
        const chipStyle =
          statusLabel === 'Pending'
            ? classes.statusChipPending
            : statusLabel === 'Locked'
              ? classes.statusChipLocked
              : statusLabel === 'Unlisted'
                ? classes.statusChipUnlisted
                : classes.statusChipLive;

        const tooltipPath = STATUS_CHIP_TOOLTIP_PATH[statusLabel];
        const chip = <Chip label={statusLabel} size="small" sx={{ ...classes.statusChip, ...chipStyle }} />;

        return (
          <TableCell
            sx={{
              ...classes.tableCellItem,
              ...(isSortedField() && classes.sortedCeil),
            }}
            style={getCellPadding(cellItem.cellPaddingRight, cellItem.cellPaddingLeft)}
            align={cellItem.textAlign || 'left'}>
            {tooltipPath ? (
              <Tooltip title={M.get(tooltipPath)} enterDelay={400} arrow>
                {chip}
              </Tooltip>
            ) : (
              chip
            )}
          </TableCell>
        );
      }
      case 'listCount': {
        const lists = data[cellItem.id];
        const count = lists?.count ?? 0;
        const names = lists?.names ?? [];
        const tooltipTitle = (
          <Box sx={classes.listCountTooltipContent}>
            {names?.map((name, index) => (
              <Box key={index} component="span" sx={classes.listCountTooltipItem}>
                {name}
              </Box>
            ))}
          </Box>
        );
        const displayValue = count ? count : '—';
        return (
          <TableCell
            sx={{
              ...classes.tableCellItem,
              ...(isSortedField() && classes.sortedCeil),
            }}
            style={getCellPadding(cellItem.cellPaddingRight, cellItem.cellPaddingLeft)}
            align={cellItem.textAlign || 'left'}>
            {count ? (
              <Tooltip
                title={tooltipTitle}
                placement="top"
                slotProps={{
                  tooltip: {
                    sx: classes.listCountTooltip,
                  },
                }}>
                <span>{displayValue}</span>
              </Tooltip>
            ) : (
              <span style={classes.listCountEmpty}>{displayValue}</span>
            )}
          </TableCell>
        );
      }
      case 'customComponent':
        return cellItem.component({
          data,
          cellItem,
          isSortedCeil: isSortedField() ? classes.sortedCeil : '',
          onIconClick,
        });
      case 'link':
        return data[cellItem.routeIdKey ?? 'id'] ? (
          <TableCell
            sx={{
              ...classes.tableCellAction,
              ...(isSortedField() && classes.sortedCeil),
              ...(cellItem.withEllipsis && classes.ellipsis),
            }}
            style={getCellPadding(cellItem.cellPaddingRight, cellItem.cellPaddingLeft)}
            align={cellItem.textAlign || 'left'}>
            <Link
              href={cellItem.route.replace(':id', data[cellItem.routeIdKey ?? 'id'])}
              target={cellItem.newTab ? '_blank' : ''}
              rel="noopener noreferrer"
              sx={classes.listItemText}>
              {data[cellItem.id]}
            </Link>
          </TableCell>
        ) : (
          defaultCell()
        );
      case 'bool':
        return (
          <TableCell
            sx={{
              ...classes.tableCellAction,
              ...(isSortedField() && classes.sortedCeil),
            }}
            style={getCellPadding(cellItem.cellPaddingRight, cellItem.cellPaddingLeft)}
            align={cellItem.textAlign || 'left'}>
            {data[cellItem.id] ? (
              cellItem.showIcon ? (
                <Tooltip title={M.get('general.yes')}>
                  <DoneIcon style={{ color: '#007f00' }} />
                </Tooltip>
              ) : (
                M.get('general.yes')
              )
            ) : cellItem.showIcon ? (
              <Tooltip title={M.get('general.no')}>
                <CloseIcon style={{ color: '#d30000' }} />
              </Tooltip>
            ) : (
              M.get('general.no')
            )}
          </TableCell>
        );
      case 'noWrap':
        return (
          <TableCell
            sx={{
              ...classes.tableCellItem,
              ...classes.noWrap,
              ...(isSortedField() && classes.sortedCeil),
              ...(cellItem.withEllipsis && classes.ellipsis),
            }}
            style={{
              ...getCellPadding(cellItem.cellPaddingRight, cellItem.cellPaddingLeft),
              ...(stickyHeader ? { minWidth: cellItem.minWidth } : getWidth(cellItem.width)),
            }}
            align={cellItem.textAlign || 'left'}>
            {data[cellItem.id]}
          </TableCell>
        );
      case 'capitalize':
        return (
          <TableCell
            sx={{
              ...classes.tableCellItem,
              ...classes.noWrap,
              ...(isSortedField() && classes.sortedCeil),
              textTransform: 'capitalize',
            }}
            style={{
              ...getCellPadding(cellItem.cellPaddingRight, cellItem.cellPaddingLeft),
              ...(stickyHeader ? { minWidth: cellItem.minWidth } : getWidth(cellItem.width)),
            }}
            align={cellItem.textAlign || 'left'}>
            {data[cellItem.id]}
          </TableCell>
        );
      default:
        return defaultCell();
    }
  };
  return <>{cellView()}</>;
}

CustomTableCell.propTypes = {
  cellItem: PropTypes.instanceOf(Object),
  data: PropTypes.instanceOf(Object),
  isCheckbox: PropTypes.bool,
  isItemSelected: PropTypes.bool,
  isEditAction: PropTypes.bool,
  isRemoveAction: PropTypes.bool,
  isResponseAction: PropTypes.bool,
  isEmptyCell: PropTypes.bool,
  loading: PropTypes.bool,
  handleClickIcon: PropTypes.func,
  isOpenAction: PropTypes.bool,
  isCloneAction: PropTypes.bool,
  openPath: PropTypes.string,
  filteredParams: PropTypes.instanceOf(Object),
  isQRAction: PropTypes.bool,
  isInMarketAction: PropTypes.bool,
  isShow: PropTypes.func,
  stickyHeader: PropTypes.bool,
  emptyRowSlot: PropTypes.node,
};

export default CustomTableCell;
